<template>
  <div>
    <breadcrumbs
      :page-title="pageTitle"
      :breadcrumb-items="breadcrumbItems"
    />

    <b-card>
      <offer-wall-toolbar />

      <div
        v-if="loading"
        class="d-flex justify-content-center py-3"
      >
        <b-spinner :label="$t('offerwall_providers.loading')" />
      </div>

      <b-form
        v-else
        @submit.prevent="submit"
      >
        <b-row>
          <b-col md="6">
            <b-form-group
              :label="$t('offerwall_providers.fields.name')"
              label-for="offerwall-provider-name"
            >
              <b-form-input
                id="offerwall-provider-name"
                v-model.trim="provider.name"
                required
                :placeholder="$t('offerwall_providers.placeholders.name')"
              />
            </b-form-group>

            <b-form-group
              :label="$t('offerwall_providers.fields.user_id')"
              label-for="offerwall-provider-user-id"
            >
              <b-form-input
                id="offerwall-provider-user-id"
                v-model.trim="provider.user_id"
                required
                :placeholder="$t('offerwall_providers.placeholders.user_id')"
              />
            </b-form-group>

            <b-form-group
              :label="$t('offerwall_providers.fields.status')"
              label-for="offerwall-provider-status"
            >
              <b-form-select
                id="offerwall-provider-status"
                v-model="provider.status"
                :options="statusOptions"
              />
            </b-form-group>
          </b-col>
        </b-row>

        <div class="d-flex mt-1">
          <b-button
            type="submit"
            variant="primary"
            :disabled="saving"
          >
            <b-spinner
              v-if="saving"
              small
              class="mr-50"
            />
            {{ $t(isEdit ? 'form.save' : 'form.create') }}
          </b-button>
          <b-button
            variant="outline-secondary"
            class="ml-1"
            :to="{ name: 'dashboard-offerwall-providers' }"
          >
            {{ $t('form.cancel') }}
          </b-button>
        </div>
      </b-form>
    </b-card>
  </div>
</template>

<script>
import {
  BButton,
  BCard,
  BCol,
  BForm,
  BFormGroup,
  BFormInput,
  BFormSelect,
  BRow,
  BSpinner,
} from 'bootstrap-vue'
import useJwt from '@/auth/jwt/useJwt'
import Breadcrumbs from '@/views/ui/breadcrumbs/Breadcrumbs.vue'
import OfferWallToolbar from '@/views/dashboard/google-ads/OfferWallToolbar.vue'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'

const emptyProvider = () => ({
  name: '',
  user_id: '',
  status: 'active',
})

export default {
  components: {
    BButton,
    BCard,
    BCol,
    BForm,
    BFormGroup,
    BFormInput,
    BFormSelect,
    BRow,
    BSpinner,
    Breadcrumbs,
    OfferWallToolbar,
  },
  data() {
    return {
      loading: false,
      saving: false,
      provider: emptyProvider(),
    }
  },
  computed: {
    isEdit() {
      return Boolean(this.$route.params.id)
    },
    pageTitle() {
      return this.$t(this.isEdit
        ? 'offerwall_providers.edit_title'
        : 'offerwall_providers.create_title')
    },
    statusOptions() {
      return [
        {
          value: 'active',
          text: this.$t('offerwall_providers.statuses.active'),
        },
        {
          value: 'inactive',
          text: this.$t('offerwall_providers.statuses.inactive'),
        },
      ]
    },
    breadcrumbItems() {
      return [
        {
          text: 'OfferWall',
          to: { name: 'dashboard-google-ads' },
        },
        {
          text: 'offerwall_providers.title',
          to: { name: 'dashboard-offerwall-providers' },
        },
        {
          text: this.isEdit
            ? 'offerwall_providers.edit'
            : 'offerwall_providers.create',
          active: true,
        },
      ]
    },
  },
  created() {
    if (this.isEdit) this.fetchProvider()
  },
  methods: {
    getErrorMessage(error) {
      return error.response?.data?.errors?.[0]?.detail
        || error.response?.data?.error
        || error.message
        || this.$t('offerwall_providers.save_error')
    },
    showToast(title, variant) {
      this.$toast({
        component: ToastificationContent,
        props: {
          title,
          icon: variant === 'success' ? 'CheckIcon' : 'AlertTriangleIcon',
          variant,
        },
      }, {
        id: `offerwall-provider-${variant}-${title}`,
        timeout: variant === 'success' ? 5000 : false,
        hideProgressBar: variant !== 'success',
        toastClassName: variant === 'success' ? 'offerwall-provider-toast-success' : '',
      })
    },
    async fetchProvider() {
      this.loading = true
      try {
        const { data } = await useJwt.getGoogleWidgetAdProvider(this.$route.params.id)
        const provider = data.googlewidgetadprovider || data.provider || data
        this.provider = {
          ...emptyProvider(),
          ...provider,
        }
      } catch (error) {
        this.showToast(this.getErrorMessage(error), 'danger')
      } finally {
        this.loading = false
      }
    },
    async submit() {
      if (this.saving) return

      this.saving = true
      try {
        if (this.isEdit) {
          await useJwt.updateGoogleWidgetAdProvider(this.$route.params.id, this.provider)
        } else {
          await useJwt.createGoogleWidgetAdProvider(this.provider)
        }
        this.showToast(
          this.$t(this.isEdit
            ? 'offerwall_providers.updated'
            : 'offerwall_providers.created'),
          'success',
        )
        this.$router.push({ name: 'dashboard-offerwall-providers' })
      } catch (error) {
        this.showToast(this.getErrorMessage(error), 'danger')
      } finally {
        this.saving = false
      }
    },
  },
}
</script>
